// Importações dos componentes
import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EventsProvider } from './contexts/EventsContext';
import { UsersProvider } from './contexts/UsersContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { ToastProvider, useToast } from './contexts/ToastContext';
import Topbar from './components/Topbar';
import Sidebar from './components/Sidebar';
import HomePage from './components/HomePage';
import CalendarPage from './components/CalendarPage';
import AdminPanel from './components/AdminPanel';
import ProfilePage from './components/ProfilePage';
import CommunityPage from './components/CommunityPage';
import MentoriasPage from './components/MentoriasPage';
import VideoPlayerPage from './components/VideoPlayerPage';
import { VideoProvider } from './contexts/VideoContext';
import SupportPage from './components/SupportPage';
import MentoresPage2 from './components/MentoresPage2';
import { MentorshipContainer } from './components/mentorship/MentorshipContainer';
import { BioLinkPage } from './components/mentorship/BioLinkPage';
import { getMentorshipBySlug } from './data/mockMentorshipData';

import UsefulLinksPage from './components/UsefulLinksPage';
import SuppliersPage from './components/SuppliersPage';
import ToastContainer from './components/ToastContainer';
import { OctoberLeaderboard } from './components/gamification/OctoberLeaderboard';
import CompetitionPage from './components/CompetitionPage';
import { FloatingChatWidget } from './components/FloatingChatWidget';
import MyProductsPage from './components/MyProductsPage';

import { getSectionFromPathname, getPathnameFromSection } from './utils/navigationUtils';

// Componente interno que tem acesso ao contexto de autenticação
function AppContent() {
  const { user, isAdmin, loading, profile } = useAuth();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { showToast } = useToast();
  
  // Roteamento dinâmico baseado no Slug de URL do navegador
  const [activeSection, setActiveSection] = useState(() => {
    const route = getSectionFromPathname(window.location.pathname, window.location.search);
    return route.section;
  });

  // Estado para subseções (ex: player:contentId:videoId ou subseções da comunidade)
  const [currentSubSection, setCurrentSubSection] = useState<string | undefined>(() => {
    const route = getSectionFromPathname(window.location.pathname, window.location.search);
    return route.subSection;
  });

  // Escutar eventos popstate do navegador (botões Voltar/Avançar e históricos)
  useEffect(() => {
    const handlePopState = () => {
      const { section, subSection } = getSectionFromPathname(window.location.pathname, window.location.search);
      setActiveSection(section);
      setCurrentSubSection(subSection);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Verificar se o usuário pode acessar a seção atual
  useEffect(() => {
    if (!loading) {
      if (activeSection === 'admin' && !isAdmin) {
        handleSectionChange('home');
      }
      if (activeSection === 'profile' && !user) {
        handleSectionChange('home');
      }
      if (activeSection === 'community' && (!user || (profile && !profile.is_active))) {
        handleSectionChange('home');
      }
      if (activeSection === 'player' && !user) {
        setIsAuthModalOpen(true);
        showToast({
          type: 'warning',
          title: 'Acesso Restrito',
          message: 'Para assistir aos vídeos da MasterClub é necessário estar logado ou cadastrado na plataforma.',
          duration: 5000
        });
      }
    }
  }, [activeSection, user, isAdmin, loading, profile, showToast]);

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    setCurrentSubSection(undefined);
    
    // Atualizar a URL na barra de endereços do navegador
    const targetPath = getPathnameFromSection(section);
    if (window.location.pathname !== targetPath && section !== 'bio-links' && !section.startsWith('mentoria-')) {
      window.history.pushState(null, '', targetPath);
    }
  };

  const handleSubSectionChange = (subSection: string) => {
    setCurrentSubSection(subSection);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'home':
        return (
          <HomePage
            onOpenContent={(contentId) => {
              setActiveSection('player');
              setCurrentSubSection(`player:${contentId}`);
            }}
            onNavigate={(section) => handleSectionChange(section)}
          />
        );
      case 'player':
        return (
          <VideoPlayerPage
            currentView={currentSubSection}
            onViewChange={handleSubSectionChange}
            onBack={() => {
              if (window.location.search) {
                const cleanUrl = window.location.origin + window.location.pathname;
                window.history.replaceState(null, '', cleanUrl);
              }
              setActiveSection('home');
              setCurrentSubSection(undefined);
            }}
          />
        );
      case 'calendar':
        return <CalendarPage />;
      case 'admin':
        return (
          <AdminPanel 
            currentView={currentSubSection}
            onViewChange={handleSubSectionChange}
          />
        );
      case 'integrations':
        return (
          <ProfilePage 
            initialTab="integrations"
            onBack={() => setActiveSection('home')}
            onNavigateToCommunity={(topicId, categoryId) => {
              setActiveSection('community');
              setCurrentSubSection(`topic:${topicId}:${categoryId}`);
            }}
          />
        );
      case 'profile':
        return (
          <ProfilePage 
            initialTab={currentSubSection === 'integrations' ? 'integrations' : 'profile'}
            onBack={() => setActiveSection('home')}
            onNavigateToCommunity={(topicId, categoryId) => {
              // Navegar para comunidade e abrir o tópico específico
              setActiveSection('community');
              // Usar uma subseção especial para indicar que deve abrir um tópico específico
              setCurrentSubSection(`topic:${topicId}:${categoryId}`);
            }}
          />
        );
      case 'leaderboard':
      case 'ranking':
        if (!isAdmin) return <div className="p-8">Ranking disponível somente para administradores.</div>;
        return (
          <OctoberLeaderboard
            onBack={() => handleSectionChange('home')}
            currentUserId={user?.id}
          />
        );
      case 'community':
        return (
          <CommunityPage 
            isAdmin={isAdmin} 
            currentView={currentSubSection}
            onViewChange={handleSubSectionChange}
          />
        );
      case 'my-products':
        return (
          <MyProductsPage 
            onBack={() => setActiveSection('home')}
            onNavigate={(section) => handleSectionChange(section)}
          />
        );
      case 'mentorias':
      case 'mentorias-2':
        return (
          <MentoriasPage 
            onBack={() => setActiveSection('home')}
          />
        );
      case 'useful-links':
        return <UsefulLinksPage />;
      case 'suppliers':
        return <SuppliersPage />;
      case 'support':
        return <SupportPage />;
      case 'competicao':
      case 'challenge':
      case 'desafio':
        return (
          <CompetitionPage 
            onBack={() => handleSectionChange('home')}
            onNavigate={(section) => handleSectionChange(section)}
          />
        );
      default:
        return (
          <HomePage 
            onNavigate={(section) => handleSectionChange(section)} 
          />
        );
    }
  };

  // Se estiver acessando a página de Bio-Links (Linktree do Mentor)
  if (
    activeSection === 'bio-links' ||
    window.location.pathname.startsWith('/bio-') ||
    window.location.pathname.startsWith('/links-') ||
    window.location.pathname.startsWith('/bio/') ||
    new URLSearchParams(window.location.search).has('bio')
  ) {
    const pathname = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const rawSlug =
      pathname.replace(/^\/(bio-|links-|bio\/)/, '') ||
      searchParams.get('bio') ||
      'vitor-terra';

    // Só passa a config estática caso seja explicitamente o perfil de demonstração vitor-terra
    const cleanSlug = rawSlug.toLowerCase();
    const initialConfig = cleanSlug === 'vitor-terra' ? getMentorshipBySlug('vitor-terra') : undefined;

    return (
      <div className="min-h-screen bg-[#0d0e11]">
        <BioLinkPage
          config={initialConfig}
          slug={rawSlug}
          onBackToMain={() => {
            setActiveSection('home');
            window.history.pushState(null, '', '/');
          }}
        />
        <ToastContainer />
      </div>
    );
  }

  // Se estiver na Área de Membros estilo Hubla (rotas /mentoria-* ou seção mentoria-hub), renderizar o container dedicado
  if (activeSection === 'mentoria-hub' || activeSection.startsWith('mentoria-') || window.location.pathname.startsWith('/mentoria-')) {
    return (
      <div className="min-h-screen bg-[#121316]">
        <MentorshipContainer
          onBackToMainPlatform={() => {
            setActiveSection('home');
            window.history.pushState(null, '', '/');
          }}
        />
        <ToastContainer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121316]">
      {/* Fixed Topbar */}
      <Topbar 
        activeSection={activeSection} 
        onSectionChange={handleSectionChange}
        currentSubSection={currentSubSection}
        onSubSectionChange={handleSubSectionChange}
        isAuthModalOpen={isAuthModalOpen}
        setIsAuthModalOpen={setIsAuthModalOpen}
      />
      
      {/* Collapsible Sidebar - Desktop Only */}
      <Sidebar 
        activeSection={activeSection} 
        onSectionChange={handleSectionChange}
        currentSubSection={currentSubSection}
        onSubSectionChange={handleSubSectionChange}
        onOpenAuth={() => setIsAuthModalOpen(true)}
      />
      
      {/* Main Content */}
      <div className="w-full md:pl-[70px] xl:pl-[80px] transition-all duration-300">
        {renderContent()}
      </div>
      
      {/* Toast Notifications */}
      <ToastContainer />

      {/* Widget de Chat Flutuante com Agente de IA e RAG */}
      <FloatingChatWidget
        currentUserId={user?.id}
        onNavigateToVideo={(contentId, videoId) => {
          setActiveSection('player');
          if (videoId) {
            setCurrentSubSection(`player:${contentId}:${videoId}`);
          } else {
            setCurrentSubSection(`player:${contentId}`);
          }
        }}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <UsersProvider>
        <EventsProvider>
          <NotificationsProvider>
            <ToastProvider>
              <VideoProvider>
                <AppContent />
              </VideoProvider>
            </ToastProvider>
          </NotificationsProvider>
        </EventsProvider>
      </UsersProvider>
    </AuthProvider>
  );
}

export default App;